// src/components/NutritionPanel.jsx  —  ohapotato v2026.10.36
// ✦ PREMIUM FEATURE — full per-serving nutrition breakdown
// Displayed inside expanded RecipeCard when user is premium.
// Falls back gracefully if nutrition data is missing.

import React, { useState } from 'react';

// Reference daily values (FDA 2000 kcal diet)
const DV = {
  calories: 2000,
  protein:  50,    // g
  carbs:    275,   // g
  fat:      78,    // g
  fibre:    28,    // g
  sodium:   2300,  // mg
  sugar:    50,    // g
};

// Traffic-light colour based on % of daily value
function dvColour(pct) {
  if (pct <= 5)  return 'var(--accent-green)';
  if (pct <= 20) return 'var(--accent-orange)';
  return '#EF4444';
}

// Colour specifically for beneficial nutrients (more = better)
function benefitColour(pct) {
  if (pct >= 20) return 'var(--accent-green)';
  if (pct >= 10) return 'var(--accent-orange)';
  return 'var(--text-muted)';
}

// Macros ring chart (pure SVG, no deps)
function MacroRing({ protein, carbs, fat }) {
  const total = protein + carbs + fat || 1;
  const pPct  = protein / total;
  const cPct  = carbs   / total;
  // fat fills the remainder

  const R    = 38;
  const circ = 2 * Math.PI * R;
  const SIZE = 92;

  // Segments: protein (green), carbs (gold), fat (orange)
  const segments = [
    { pct: pPct, colour: 'var(--accent-green)',  offset: 0 },
    { pct: cPct, colour: 'var(--accent-gold)',   offset: pPct },
    { pct: 1 - pPct - cPct, colour: 'var(--accent-orange)', offset: pPct + cPct },
  ];

  return (
    <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}
      style={{ transform: 'rotate(-90deg)', display: 'block', flexShrink: 0 }}>
      {/* Track */}
      <circle cx={SIZE/2} cy={SIZE/2} r={R} fill="none"
        stroke="var(--border-base)" strokeWidth="10" />
      {/* Segments */}
      {segments.map((seg, i) => (
        <circle key={i}
          cx={SIZE/2} cy={SIZE/2} r={R}
          fill="none"
          stroke={seg.colour}
          strokeWidth="10"
          strokeDasharray={`${circ * seg.pct} ${circ * (1 - seg.pct)}`}
          strokeDashoffset={`${-circ * seg.offset}`}
          strokeLinecap="butt"
        />
      ))}
    </svg>
  );
}

// Single macro stat pill
function MacroStat({ label, value, unit, colour }) {
  return (
    <div style={styles.macroStat}>
      <div style={{ ...styles.macroDot, background: colour }} />
      <div>
        <div style={styles.macroVal}>{value}<span style={styles.macroUnit}>{unit}</span></div>
        <div style={styles.macroLabel}>{label}</div>
      </div>
    </div>
  );
}

// Bar row for micronutrients
function NutrientBar({ label, value, unit, dv, beneficial = false }) {
  const pct     = Math.min(100, Math.round((value / dv) * 100));
  const colour  = beneficial ? benefitColour(pct) : dvColour(pct);
  return (
    <div style={styles.barRow}>
      <div style={styles.barMeta}>
        <span style={styles.barLabel}>{label}</span>
        <span style={styles.barValue}>{value}{unit}</span>
      </div>
      <div style={styles.barTrack}>
        <div style={{
          ...styles.barFill,
          width: `${pct}%`,
          background: colour,
        }} />
      </div>
      <span style={{ ...styles.barDv, color: colour }}>{pct}% DV</span>
    </div>
  );
}

export default function NutritionPanel({ nutrition, servings }) {
  const [expanded, setExpanded] = useState(true);

  // Always render the teaser row; full detail is premium-gated
  const n = nutrition || {};
  const hasData = n.calories > 0 || n.protein > 0;

  if (!hasData) {
    // No nutrition data yet — happens for non-Anthropic backends
    return (
      <div style={styles.noData}>
        <span style={styles.premiumPill}>✦ Premium</span>
        <span style={styles.noDataText}>Nutrition data is generated by AI for each recipe.</span>
      </div>
    );
  }

  const calsFromMacros = ((n.protein || 0) * 4) + ((n.carbs || 0) * 4) + ((n.fat || 0) * 9);
  const displayCals    = n.calories || calsFromMacros;

  if (false) { // premium gate removed — nutrition open to all
    // Teaser: show calories only, blur the rest
    return (
      <div style={styles.teaser}>
        <span style={styles.premiumPill}>✦ Premium</span>
        <div style={styles.teaserRow}>
          <div style={styles.teaserCal}>
            <span style={styles.teaserCalNum}>{displayCals}</span>
            <span style={styles.teaserCalLabel}>cal / serving</span>
          </div>
          <div style={styles.teaserBlurred}>
            <div style={styles.blurBlock} />
            <div style={{ ...styles.blurBlock, width: '60%' }} />
          </div>
          <div style={styles.teaserCta}>
            Full breakdown with Premium
          </div>
        </div>
      </div>
    );
  }

  // Full premium view
  return (
    <div style={styles.wrap}>
      {/* Header */}
      <button style={styles.toggleBtn} onClick={() => setExpanded(e => !e)}>
        <span style={styles.premiumPill}>✦ Premium</span>
        <span style={styles.toggleLabel}>Nutrition breakdown</span>
        <span style={styles.toggleChev}>{expanded ? '▴' : '▾'}</span>
      </button>

      {expanded && (
        <div style={styles.body}>

          {/* Calories hero + macro ring */}
          <div style={styles.macroHero}>
            <div style={styles.ringWrap}>
              <MacroRing
                protein={n.protein || 0}
                carbs={n.carbs || 0}
                fat={n.fat || 0}
              />
              <div style={styles.ringCenter}>
                <span style={styles.calsNum}>{displayCals}</span>
                <span style={styles.calsLabel}>cal</span>
              </div>
            </div>

            <div style={styles.macroStats}>
              <MacroStat label="Protein" value={n.protein || 0} unit="g" colour="var(--accent-green)" />
              <MacroStat label="Carbs"   value={n.carbs   || 0} unit="g" colour="var(--accent-gold)" />
              <MacroStat label="Fat"     value={n.fat     || 0} unit="g" colour="var(--accent-orange)" />
            </div>
          </div>

          <p style={styles.perServing}>Per serving · {servings} serving{servings !== '1' ? 's' : ''} total</p>

          {/* Micronutrients */}
          <div style={styles.micros}>
            {n.fibre  != null && (
              <NutrientBar label="Fibre"  value={n.fibre}  unit="g"  dv={DV.fibre}  beneficial />
            )}
            {n.sugar  != null && (
              <NutrientBar label="Sugar"  value={n.sugar}  unit="g"  dv={DV.sugar} />
            )}
            {n.sodium != null && (
              <NutrientBar label="Sodium" value={n.sodium} unit="mg" dv={DV.sodium} />
            )}
          </div>

          {/* DV legend */}
          <p style={styles.dvNote}>
            % DV based on a 2,000 kcal/day diet. Values are estimates.
          </p>
        </div>
      )}
    </div>
  );
}

const styles = {
  // No data state
  noData: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '10px 0',
  },
  noDataText: {
    fontSize: '12px', color: 'var(--text-muted)',
    fontFamily: 'var(--font-body)',
  },

  // Teaser (non-premium)
  teaser: {
    background: 'var(--bg-pill)',
    border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 14px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  teaserRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
  },
  teaserCal: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    background: 'var(--bg-card)', borderRadius: 'var(--radius-md)',
    padding: '8px 12px', flexShrink: 0,
    border: '1px solid var(--border-base)',
  },
  teaserCalNum: {
    fontFamily: 'var(--font-body)', fontSize: '22px', fontWeight: '700',
    color: 'var(--text-primary)', lineHeight: '1',
  },
  teaserCalLabel: {
    fontSize: '10px', color: 'var(--text-muted)',
    fontFamily: 'var(--font-body)', marginTop: '2px',
  },
  teaserBlurred: {
    flex: 1, display: 'flex', flexDirection: 'column', gap: '6px',
  },
  blurBlock: {
    height: '10px', width: '100%',
    background: 'var(--border-base)',
    borderRadius: 'var(--radius-pill)',
    filter: 'blur(2px)',
  },
  teaserCta: {
    fontSize: '11px', fontWeight: '600',
    color: 'var(--accent-gold)', fontFamily: 'var(--font-body)',
    whiteSpace: 'nowrap', flexShrink: 0,
  },

  // Premium pill
  premiumPill: {
    display: 'inline-block', fontSize: '9px', fontWeight: '700',
    letterSpacing: '0.4px', color: 'var(--accent-gold)',
    background: 'var(--accent-gold-bg)', padding: '2px 8px',
    borderRadius: 'var(--radius-pill)', fontFamily: 'var(--font-body)',
    flexShrink: 0,
  },

  // Full panel
  wrap: {
    border: '1.5px solid var(--accent-gold)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
    background: 'var(--bg-card)',
    transition: 'background 0.3s ease',
    marginTop: '4px',
  },
  toggleBtn: {
    width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
    padding: '10px 14px',
    background: 'var(--accent-gold-bg)', border: 'none', cursor: 'pointer',
    fontFamily: 'var(--font-body)', textAlign: 'left',
    borderBottom: '1px solid var(--border-base)',
  },
  toggleLabel: {
    flex: 1, fontSize: '12.5px', fontWeight: '600',
    color: 'var(--text-primary)',
  },
  toggleChev: {
    fontSize: '11px', color: 'var(--text-muted)',
  },

  body: { padding: '0 14px 14px' },

  // Macro hero
  macroHero: {
    display: 'flex', alignItems: 'center', gap: '16px',
    paddingBottom: '12px', borderBottom: '1px solid var(--border-base)',
    marginBottom: '12px',
  },
  ringWrap: { position: 'relative', width: '92px', height: '92px', flexShrink: 0 },
  ringCenter: {
    position: 'absolute', inset: 0,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
  },
  calsNum: {
    fontFamily: 'var(--font-body)', fontSize: '18px', fontWeight: '700',
    color: 'var(--text-primary)', lineHeight: '1',
  },
  calsLabel: {
    fontSize: '10px', color: 'var(--text-muted)',
    fontFamily: 'var(--font-body)',
  },
  macroStats: {
    flex: 1, display: 'flex', flexDirection: 'column', gap: '8px',
  },
  macroStat: {
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  macroDot: {
    width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
  },
  macroVal: {
    fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: '700',
    color: 'var(--text-primary)', lineHeight: '1',
  },
  macroUnit: { fontSize: '11px', fontWeight: '400', marginLeft: '1px' },
  macroLabel: {
    fontSize: '10px', color: 'var(--text-muted)',
    fontFamily: 'var(--font-body)',
  },

  perServing: {
    fontSize: '11px', color: 'var(--text-muted)',
    fontFamily: 'var(--font-body)', marginBottom: '10px',
  },

  // Micro bars
  micros: {
    display: 'flex', flexDirection: 'column', gap: '8px',
    marginBottom: '10px',
  },
  barRow: {
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  barMeta: {
    display: 'flex', justifyContent: 'space-between',
    width: '100px', flexShrink: 0,
  },
  barLabel: {
    fontSize: '11.5px', color: 'var(--text-secondary)',
    fontFamily: 'var(--font-body)',
  },
  barValue: {
    fontSize: '11.5px', fontWeight: '600', color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)',
  },
  barTrack: {
    flex: 1, height: '6px',
    background: 'var(--border-base)', borderRadius: 'var(--radius-pill)',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%', borderRadius: 'var(--radius-pill)',
    transition: 'width 0.5s ease',
  },
  barDv: {
    fontSize: '10.5px', fontWeight: '600',
    fontFamily: 'var(--font-body)', width: '46px',
    textAlign: 'right', flexShrink: 0,
  },

  dvNote: {
    fontSize: '10.5px', color: 'var(--text-muted)',
    fontFamily: 'var(--font-body)', lineHeight: '1.4',
    borderTop: '1px solid var(--border-base)',
    paddingTop: '8px', marginTop: '2px',
  },
};
